/** 绩效分析（G5 · analytics）。
 *
 * 规格 `docs/design/screens/analytics.md`：
 * - 历史回测：`GET /validation/backtest`（someo-backtrader 修正版，后端按 keel 算式重算，B7）；
 * - 回测 vs 纸上盘：回测一侧取上面的样本内 / 样本外，纸上盘一侧取 `GET /validation/{sid}` 的
 *   `latest_weekly.out_of_sample`，同名指标、同为权益口径；
 * - 归因报告：`GET /attribution/report?strategy=`，四层对应表未定前一律「缺」（B8）。
 * 本屏只读，不在前端算任何指标。
 */

import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { isRetryable } from "../../api/errors";
import { Card, Kv, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { ErrorState, Loading, QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { readAttributionReport, readBacktest, residualVerdict, type AttributionReport, type BacktestPayload, type Reported, type Segment } from "../../lib/analytics";
import { DASH, num, pct, short, signedPct } from "../../lib/format";
import { basisLabel, readValidationDetail, type ValidationDetail } from "../../lib/validation";

const READ = { retry: (count: number, error: unknown) => count < 2 && isRetryable(error), retryDelay: 1_500 } as const;
const MISSING = "缺";

const ann = (s: Segment | null) => (s ? signedPct(s.annualized_return) : MISSING);
const dd = (s: Segment | null) => (s ? `−${pct(s.max_drawdown)}` : MISSING);
const sharpe = (s: Segment | null) => (s ? num(s.sharpe, 2) : MISSING);
const rep = (r: Reported | null) =>
  r ? `${signedPct(r.annualized_return)} / −${pct(r.max_drawdown)} / ${num(r.sharpe, 2)}` : MISSING;

export function AnalyticsScreen(): ReactNode {
  const bt = useQuery<BacktestPayload, Error>({ queryKey: ["validation", "backtest"], queryFn: readBacktest, ...READ });
  const strategies = (bt.data?.strategies ?? []).map((s) => s.strategy_id).filter((s): s is string => s !== null);
  const [sid, setSid] = useState<string>("");
  const selected = sid || strategies[0] || "";
  return (
    <>
      <PageHead title="绩效分析" sub="GET /validation/backtest · GET /attribution/report" />
      <Lede>
        四条策略的历史回测（修正成交假设与成本后）全区间与样本内外各赚不赚钱；纸上盘与回测放在同一套算式、同一权益口径下比；
        绩效差异拆到数据 / 参数 / 策略 / 执行四层。
      </Lede>
      <QueryState status={bt.status} error={bt.error} what="历史回测绩效（strategy-validation「历史回测绩效」）" refetch={() => void bt.refetch()}>
        {bt.data ? <Backtest data={bt.data} /> : null}
      </QueryState>
      <div className="fieldrow" style={{ marginTop: 12 }}>
        <label className="f">
          <span>对比 / 归因的策略</span>
          <select value={selected} onChange={(e) => setSid(e.target.value)}>
            {strategies.length === 0 ? <option value="">（回测结果不可用）</option> : null}
            {strategies.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>
      {selected ? (
        <div className="grid g2" style={{ marginTop: 12 }}>
          <Compare sid={selected} backtest={bt.data ?? null} />
          <Attribution sid={selected} />
        </div>
      ) : null}
    </>
  );
}

function Backtest({ data }: { data: BacktestPayload }): ReactNode {
  return (
    <Card
      title="历史回测（修正版）"
      sub={`快照 ${data.source.snapshot_id ?? MISSING} · 区间 ${data.source.period ? `${data.source.period.start} – ${data.source.period.end}` : MISSING} · 回测仓提交号：${data.source.repo_commit ?? MISSING}`}
      nopad
    >
      <Table
        cols={[
          { label: "策略" },
          { label: "全区间年化", num: true },
          { label: "全区间回撤", num: true },
          { label: "全区间 Sharpe", num: true },
          { label: "样本内年化", num: true },
          { label: "样本内回撤", num: true },
          { label: "样本外年化", num: true },
          { label: "样本外回撤", num: true },
          { label: "样本外 Sharpe", num: true },
          { label: "区间（内 / 外）", wrap: true },
          { label: "回测版本", mono: true },
          { label: "修正项", wrap: true },
          { label: "回测仓自报（全区间 年化 / 回撤 / Sharpe）", wrap: true },
        ]}
        rows={data.strategies.map((s) => {
          const c = s.corrected;
          if (c === null) {
            return { key: s.backtest_id, cls: "warnrow", cells: [label(s.strategy_id, s.backtest_id), "缺：修正版未跑", "", "", "", "", "", "", "", "", "", "", ""] };
          }
          return {
            key: s.backtest_id,
            cells: [
              label(s.strategy_id, s.backtest_id),
              ann(c.full),
              dd(c.full),
              sharpe(c.full),
              ann(c.sample_in),
              dd(c.sample_in),
              ann(c.sample_out),
              dd(c.sample_out),
              sharpe(c.sample_out),
              `${c.sample_in ? c.sample_in.period.join(" – ") : MISSING} / ${c.sample_out ? c.sample_out.period.join(" – ") : MISSING}`,
              short(c.source_digest, 8),
              c.corrections.join("、") || DASH,
              rep(c.reported.full),
            ],
          };
        })}
        empty="回测产物里没有策略"
      />
      <div className="body">
        <Note>
          指标由后端 <code>{data.formula.engine}</code> 对修正版权益曲线重算（{data.formula.basis === "equity" ? "权益口径" : data.formula.basis}，
          无风险利率 {pct(data.formula.risk_free_annual)}），与纸上盘周报同一算式；最后一列是回测仓自报的另一族数字，只作对照。
          {data.formula.sample_in_note ? ` ${data.formula.sample_in_note}。` : ""}
        </Note>
        <Note>产物：<code>{data.source.path}</code> · sha256 {short(data.source.sha256, 12)}</Note>
      </div>
    </Card>
  );
}

function label(sid: string | null, backtestId: string): string {
  return sid ? `${sid}（${backtestId}）` : `${backtestId}（未映射）`;
}

function Compare({ sid, backtest }: { sid: string; backtest: BacktestPayload | null }): ReactNode {
  const q = useQuery<ValidationDetail, Error>({ queryKey: ["validation", sid], queryFn: () => readValidationDetail(sid), ...READ });
  const bt = backtest?.strategies.find((s) => s.strategy_id === sid)?.corrected ?? null;
  const paper = q.data?.latest_weekly?.out_of_sample ?? null;
  return (
    <Card title="回测 vs 纸上盘" sub={`${sid} · 同一算式、同名指标、样本内外分列`}>
      {q.status === "pending" ? <Loading what="纸上盘周报" /> : null}
      {q.status === "error" ? <ErrorState error={q.error} what="纸上盘周报（GET /validation/{sid}）" onRetry={() => void q.refetch()} /> : null}
      <Table
        cols={[{ label: "指标" }, { label: "回测 · 样本内", num: true }, { label: "回测 · 样本外", num: true }, { label: "纸上盘 · 样本外", num: true }]}
        rows={[
          { key: "ann", cells: ["年化收益", ann(bt?.sample_in ?? null), ann(bt?.sample_out ?? null), paper ? signedPct(paper.annualized_return) : DASH] },
          { key: "dd", cells: ["最大回撤", dd(bt?.sample_in ?? null), dd(bt?.sample_out ?? null), paper ? `−${pct(paper.max_drawdown)}` : DASH] },
          { key: "sh", cells: ["Sharpe", sharpe(bt?.sample_in ?? null), sharpe(bt?.sample_out ?? null), paper ? num(paper.sharpe, 2) : DASH] },
          {
            key: "days",
            cells: [
              "交易日",
              bt?.sample_in ? String(bt.sample_in.trading_days) : MISSING,
              bt?.sample_out ? String(bt.sample_out.trading_days) : MISSING,
              paper ? `${paper.trading_days}（${paper.start_date} – ${paper.end_date}）` : DASH,
            ],
          },
        ]}
      />
      {q.status === "success" && paper === null ? <Note>纸上盘还没有走查周报，无法对比。</Note> : null}
      {paper ? (
        <Note tone={paper.trading_days < 20 ? "warn" : ""}>
          纸上盘出处：GET /validation/{sid} latest_weekly（{q.data?.latest_weekly?.as_of}），{basisLabel(paper)}
          {paper.trading_days < 20 ? "；样本太短，仅作参考" : ""}。
        </Note>
      ) : null}
      <Note>回测出处：GET /validation/backtest（keel 算式重算）。</Note>
    </Card>
  );
}

function Attribution({ sid }: { sid: string }): ReactNode {
  const q = useQuery<AttributionReport, Error>({ queryKey: ["attribution", "report", sid], queryFn: () => readAttributionReport(sid), ...READ });
  return (
    <Card title="归因报告" sub="trade-attribution「归因报告」· 数据 / 参数 / 策略 / 执行">
      {q.status === "pending" ? <Loading what="归因报告" /> : null}
      {q.status === "error" ? <ErrorState error={q.error} what="归因报告（GET /attribution/report）" onRetry={() => void q.refetch()} /> : null}
      {q.data ? <AttributionBody report={q.data} /> : null}
    </Card>
  );
}

function AttributionBody({ report }: { report: AttributionReport }): ReactNode {
  const verdict = residualVerdict(report);
  return (
    <>
      <Table
        cols={[{ label: "层" }, { label: "分摊额（年化）", num: true }, { label: "状态" }]}
        rows={report.layers.map((l) => ({
          key: l.layer,
          cells: [l.layer, l.status === "缺" ? MISSING : signedPct(l.contribution), <Pill key="s" tone={l.status === "缺" ? "warn" : "ok"}>{l.status}</Pill>],
        }))}
      />
      <Kv
        pairs={[
          ["总差异", report.total_diff === null ? DASH : signedPct(report.total_diff)],
          ["残差", verdict === "unknown" ? DASH : signedPct(report.residual)],
          ["主要来源", report.main_source ?? DASH],
        ]}
      />
      {verdict === "unbalanced" ? <Note tone="bad">四层之和与总差异差 {signedPct(report.residual)}</Note> : null}
      {report.reason ? <Note tone="warn">{report.reason}</Note> : null}
      {report.candidate ? (
        <Note>
          可用的候选来源：someo-backtrader {report.candidate.backtest_id} 的{report.candidate.kind}，共 {report.candidate.items} 项；未归入四层前不显示数字。
        </Note>
      ) : null}
    </>
  );
}
